import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  FolderOpen, 
  Users, 
  Shield, 
  Settings, 
  Bell,
  Search,
  Package,
  LogOut,
  Moon,
  Sun,
  Mail,
  Inbox,
  BarChart3,
  Upload,
  Send,
  History,
  Building2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';

const Sidebar = () => {
  const { user, logout, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/projects', icon: FolderOpen, label: 'Projects' },
    { to: '/documents', icon: FileText, label: 'Documents' },
    { to: '/correspondence', icon: Mail, label: 'Correspondence' },
    { to: '/transmittals', icon: Send, label: 'Transmittals' },
    { to: '/assets', icon: Package, label: 'Assets' },
    { to: '/search', icon: Search, label: 'Search' },
    { to: '/reports', icon: BarChart3, label: 'Reports' },
  ];

  const adminItems = [
    { to: '/users', icon: Users, label: 'Users', permission: 'manage_users' },
    { to: '/roles', icon: Shield, label: 'Roles', permission: 'admin' },
    { to: '/contacts', icon: Building2, label: 'Contacts', permission: 'admin' },
    { to: '/mail-import', icon: Inbox, label: 'Project Mailbox', permission: 'admin' },
    { to: '/bulk-import', icon: Upload, label: 'Bulk Import', permission: 'admin' },
    { to: '/audit-trail', icon: History, label: 'Audit Trail', permission: 'admin' },
  ];

  return (
    <aside className="sidebar">
      <div className="p-4 border-b border-border">
        <img 
          src="https://customer-assets.emergentagent.com/job_325deca4-9e7a-442a-9f73-3e86fea2a934/artifacts/ogbckvza_DocuDesk.png" 
          alt="DocuDesk" 
          className="h-20 w-auto"
        />
      </div>

      <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
        <div className="space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        {adminItems.some(item => !item.permission || hasPermission(item.permission)) && (
          <>
            <div className="mt-6 mb-2 px-4">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Administration
              </span>
            </div>
            <div className="space-y-1">
              {adminItems.map((item) => (
                (!item.permission || hasPermission(item.permission)) && (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? 'active' : ''}`
                    }
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-4 h-4" strokeWidth={1.5} />
                    <span>{item.label}</span>
                  </NavLink>
                )
              ))}
            </div>
          </>
        )}

        <div className="mt-6 mb-2 px-4">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Settings
          </span>
        </div>
        <div className="space-y-1">
          <NavLink
            to="/notifications"
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''}`
            }
            data-testid="nav-notifications"
          >
            <Bell className="w-4 h-4" strokeWidth={1.5} />
            <span>Notifications</span>
          </NavLink>
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''}`
            }
            data-testid="nav-settings"
          >
            <Settings className="w-4 h-4" strokeWidth={1.5} />
            <span>Settings</span>
          </NavLink>
        </div>
      </nav>

      <div className="p-4 border-t border-border space-y-3">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className="w-full justify-start gap-2"
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? (
              <>
                <Sun className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">Light Mode</span>
              </>
            ) : (
              <>
                <Moon className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">Dark Mode</span>
              </>
            )}
          </Button>
        </div>
        
        <div className="flex items-center gap-3 px-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-medium">
              {user?.full_name?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.full_name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.role_name}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="shrink-0"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
